import { Link } from "react-router-dom";

export default function Terms() {
  return (
    <div style={page}>
      <h1 style={heading}>Terms of Service</h1>
      <p style={updated}>Last updated: {LAST_UPDATED}</p>

      <p style={disclaimer}>
        This is a general template, not legal advice. Have a lawyer review it
        before relying on it for compliance in your country.
      </p>

      <Section title="1. Acceptance of these terms">
        By creating an account or using GatherYou (the "Service"), you agree
        to these Terms of Service. If you don't agree, please don't use the
        Service.
      </Section>

      <Section title="2. What GatherYou is">
        GatherYou is a social platform for sharing and discovering Punjabi
        fashion and embroidery designs. Users can post designs, follow other
        users, like, comment, save, and download shared content.
      </Section>

      <Section title="3. Your account">
        You must be at least 18 years old to create an account or use the
        Service. You're responsible for keeping your login credentials secure
        and for all activity under your account. You must provide accurate
        information when signing up, and you may not create an account for
        anyone other than yourself.
      </Section>

      <Section title="4. Content you post">
        You retain ownership of any design, photo, caption, or comment you
        post. By posting, you grant GatherYou a non-exclusive, worldwide
        license to host, display, and distribute that content within the
        Service so other users can view it. You're responsible for making
        sure you have the right to post whatever you upload.
      </Section>

      <Section title="5. Prohibited conduct">
        You agree not to:
        <ul style={list}>
          <li>Post content that infringes someone else's copyright or intellectual property</li>
          <li>Harass, threaten, or impersonate other users</li>
          <li>Post spam, scams, or misleading content</li>
          <li>Attempt to access another user's account without permission</li>
          <li>Use the Service for anything illegal</li>
          <li>
            Scrape, crawl, or use automated tools to access or collect data
            from the Service without our written permission
          </li>
          <li>
            Redistribute, resell, or use another user's design commercially
            without their permission, even if you downloaded or saved it
            through the Service
          </li>
        </ul>
      </Section>

      <Section title="6. Reporting and moderation">
        Users can report content or accounts that violate these terms. We may
        remove content, suspend, or terminate accounts that violate these
        terms, at our discretion.
      </Section>

      <Section title="7. Copyright complaints">
        If you believe content on the Service infringes your copyright and
        you have an account, you can report the design directly using its
        "Report" option. If you don't have an account, or the issue isn't
        resolved through that report, contact the Grievance Officer
        (Section 16) with: a description of the copyrighted work, a link to
        the infringing content, and your contact details. We will review the
        complaint and may remove the content while it's investigated.
      </Section>

      <Section title="8. Third-party services">
        Sharing a design may open a third-party app (like WhatsApp or
        Telegram). Your use of those services is governed by their own
        terms, not ours.
      </Section>

      <Section title="9. Disclaimer">
        The Service is provided "as is," without warranties of any kind. We
        don't guarantee the Service will be uninterrupted, error-free, or
        that content posted by other users is accurate or appropriate.
      </Section>

      <Section title="10. Limitation of liability">
        To the fullest extent permitted by law, GatherYou is not liable for
        any indirect, incidental, or consequential damages arising from your
        use of the Service.
      </Section>

      <Section title="11. Changes to these terms">
        We may update these terms from time to time. Continued use of the
        Service after changes means you accept the updated terms.
      </Section>

      <Section title="12. Account termination">
        You may delete your account at any time from Profile settings.
        We may suspend or terminate your account if you violate these terms,
        as described in Section 6. When an account is deleted, its content
        is removed from the Service; some information may be retained where
        needed for legal, security, or record-keeping purposes.
      </Section>

      <Section title="13. Governing law and jurisdiction">
        These terms are governed by the laws of India. Any dispute arising
        from these terms or your use of the Service will be subject to the
        exclusive jurisdiction of the courts of Jaito, India.
      </Section>

      <Section title="14. Indemnification">
        You agree to indemnify and hold GatherYou harmless from any claims,
        damages, or expenses (including legal fees) arising from your use of
        the Service, your content, or your violation of these terms.
      </Section>

      <Section title="15. Severability">
        If any part of these terms is found unenforceable, the rest will
        remain in effect.
      </Section>

      <Section title="16. Grievance officer">
        In accordance with the Information Technology Act, 2000 and the
        rules made thereunder, including the Information Technology
        (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021,
        the details of the Grievance Officer are provided below:
        <div style={grievanceBox}>
          Name: Kamaljeet Singh
          <br />
          Email: contact@gatheryou.in
          <br />
          Address: Jaitu, Faridkot, Punjab, India
        </div>
        The Grievance Officer will acknowledge complaints and take action as
        required under applicable law.
      </Section>

      <Section title="17. Contact">
        Questions about these terms? Email us at{" "}
        <a href="mailto:contact@gatheryou.in" style={link}>contact@gatheryou.in</a>.
      </Section>

      <p style={backLink}>
        <Link to="/privacy" style={link}>Privacy Policy</Link>
        {" · "}
        <Link to="/help" style={link}>Help &amp; FAQ</Link>
        {" · "}
        <Link to="/" style={link}>Back to GatherYou</Link>
      </p>
    </div>
  );
}

const LAST_UPDATED = "November 2026";

function Section({ title, children }) {
  return (
    <section style={section}>
      <h2 style={sectionTitle}>{title}</h2>
      <div style={sectionBody}>{children}</div>
    </section>
  );
}

const page = {
  padding: "24px 20px calc(90px + env(safe-area-inset-bottom))",
  maxWidth: 680,
  margin: "0 auto",
  background: "var(--bg-page)",
  minHeight: "100vh",
};

const heading = {
  fontFamily: "'Fraunces', serif",
  fontSize: 26,
  color: "var(--text-heading)",
  margin: "0 0 4px",
};

const updated = { fontSize: 12.5, color: "var(--text-muted)", margin: "0 0 16px" };

const disclaimer = {
  fontSize: 12.5,
  color: "var(--text-body)",
  background: "var(--bg-soft)",
  border: "1px solid var(--border-soft)",
  borderRadius: 10,
  padding: "10px 14px",
  marginBottom: 24,
};

const section = { marginBottom: 22 };

const sectionTitle = {
  fontSize: 15.5,
  color: "var(--text-heading)",
  margin: "0 0 8px",
};

const sectionBody = {
  fontSize: 14,
  color: "var(--text-body)",
  lineHeight: 1.6,
};

const list = { margin: "8px 0 0", paddingLeft: 20 };

const grievanceBox = {
  marginTop: 10,
  padding: "10px 14px",
  background: "var(--bg-soft)",
  border: "1px solid var(--border-soft)",
  borderRadius: 10,
  fontSize: 13.5,
  lineHeight: 1.7,
};

const backLink = {
  fontSize: 13.5,
  color: "var(--text-muted)",
  marginTop: 32,
  textAlign: "center",
};

const link = { color: "var(--accent-link)", fontWeight: 600, textDecoration: "none" };
